import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as R from 'ramda'
import { t } from 'ttag'
import { getLocation } from 'ducks/router'
import { mapInternalStateToPayload } from 'components/modals/DepartmentManagerModal/actions'
import { useModal, useSearch } from 'hooks'
import { departments } from 'orm'
import { DEFAULT_DEPARTMENTS_QUERY_PARAMS } from 'constants/departments'

const DEFAULT_DEPARTMENT_STATE = { id: null, name: null }

const useDepartmentManagerModal = (state, handlers) => {
  const { departmentId } = state
  const { setDepartment } = handlers

  const { handleClose, handleOpen, isOpen } = useModal()

  const dispatch = useDispatch()

  const { query = {} } = useSelector(getLocation)

  const handleConfirm = React.useCallback(
    async (data, { onError, onSuccess }) => {
      const payload = mapInternalStateToPayload(data)
      const isEditing = !R.isNil(departmentId)

      const apiMethod = isEditing
        ? departments.patchStart
        : departments.postStart

      const message = isEditing
        ? t`Department was successfully updated.`
        : t`Department was successfully created.`

      dispatch(
        apiMethod({
          ...(isEditing ? { pk: departmentId } : {}),
          data: payload,
          message,
          onFailureAction: onError,
          onSuccessAction: departments.fetchStart(
            query ?? DEFAULT_DEPARTMENTS_QUERY_PARAMS,
          ),
          postSuccessAction: () => {
            handleClose()
            setDepartment(DEFAULT_DEPARTMENT_STATE)
            onSuccess()
          },
        }),
      )
    },
    [query, dispatch, handleClose, departmentId],
  )

  return {
    handleDepartmentManagerClose: handleClose,
    handleDepartmentManagerConfirm: handleConfirm,
    handleDepartmentManagerOpen: handleOpen,
    isDepartmentManagerOpen: isOpen,
  }
}

const useDeleteDepartmentModal = (state, handlers) => {
  const { departmentId } = state
  const { handleDeleteDepartment, handleResetDepartment } = handlers

  const { handleClose, handleOpen, isOpen } = useModal()

  const handleDeleteDepartmentClose = React.useCallback(() => {
    handleClose()
    handleResetDepartment()
  }, [handleClose, handleResetDepartment])

  const handleDeleteDepartmentConfirm = React.useCallback(() => {
    handleDeleteDepartment(
      { departmentId },
      { postSuccessAction: handleDeleteDepartmentClose },
    )
  }, [departmentId, handleDeleteDepartment, handleDeleteDepartmentClose])

  return {
    handleDeleteDepartmentClose,
    handleDeleteDepartmentConfirm,
    handleDeleteDepartmentOpen: handleOpen,
    isDeleteDepartmentOpen: isOpen,
  }
}

const useDeleteDepartmentWarningModal = handlers => {
  const { handleResetDepartment } = handlers

  const { handleClose, handleOpen, isOpen } = useModal()

  const handleDeleteDepartmentWarningClose = React.useCallback(() => {
    handleClose()
    handleResetDepartment()
  }, [handleClose, handleResetDepartment])

  return {
    handleDeleteDepartmentWarningClose: handleDeleteDepartmentWarningClose,
    handleDeleteDepartmentWarningConfirm: handleDeleteDepartmentWarningClose,
    handleDeleteDepartmentWarningOpen: handleOpen,
    isDeleteDepartmentWarningOpen: isOpen,
  }
}

export const useDepartments = () => {
  const dispatch = useDispatch()

  const [search, setSearch] = useSearch()
  const [department, setDepartment] = React.useState(DEFAULT_DEPARTMENT_STATE)

  const handleResetDepartment = () => setDepartment(DEFAULT_DEPARTMENT_STATE)
  const departmentId = department?.id

  const handleDeleteDepartment = (
    { departmentId },
    { postSuccessAction } = {},
  ) => {
    dispatch(
      departments.deleteStart({
        message: t`Department was successfully deleted.`,
        pk: R.toString(departmentId),
        postSuccessAction,
      }),
    )
  }

  const {
    handleDepartmentManagerClose,
    handleDepartmentManagerConfirm,
    handleDepartmentManagerOpen,
    isDepartmentManagerOpen,
  } = useDepartmentManagerModal({ departmentId }, { setDepartment })

  const {
    handleDeleteDepartmentWarningClose,
    handleDeleteDepartmentWarningConfirm,
    handleDeleteDepartmentWarningOpen,
    isDeleteDepartmentWarningOpen,
  } = useDeleteDepartmentWarningModal({ handleResetDepartment })

  const {
    handleDeleteDepartmentClose,
    handleDeleteDepartmentConfirm,
    handleDeleteDepartmentOpen,
    isDeleteDepartmentOpen,
  } = useDeleteDepartmentModal(
    { departmentId },
    { handleDeleteDepartment, handleResetDepartment },
  )

  const handleClose = () => {
    handleDepartmentManagerClose()
    handleResetDepartment()
  }

  const handleEdit = React.useCallback(
    department => {
      setDepartment(department)
      handleDepartmentManagerOpen()
    },
    [handleDepartmentManagerOpen],
  )

  const handleDelete = React.useCallback(
    department => {
      setDepartment(department)
      handleDeleteDepartmentOpen()
    },
    [handleDeleteDepartmentOpen],
  )

  return {
    state: {
      search,
      department,
      isDepartmentManagerOpen,
      isDeleteDepartmentOpen,
      isDeleteDepartmentWarningOpen,
    },
    handlers: {
      handleDepartmentManagerClose: handleClose,
      handleDepartmentManagerConfirm,
      handleDepartmentManagerOpen,
      handleDeleteDepartmentClose,
      handleDeleteDepartmentConfirm,
      handleDeleteDepartmentWarningClose,
      handleDeleteDepartmentWarningConfirm,
      handleDeleteDepartmentWarningOpen,
      handleEdit,
      handleDelete,
      setSearch,
    },
  }
}
